import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import responseHandler from "responseHandler";
import { MSpinner } from "components/Shared";
import { Button } from "react-md";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_STRING, DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { PermissionToExtendStart } from "interfaces/Mueller.Business.Models";
import { requestPermissionToExtend, getPermissionToExtendTemplates } from "requests/dueDateExtend";

interface UWPermissionToExtendProps {
  // component props
  controlIdNumber: number;
  sentCallback: () => void;
  // redux props
  makeSuccessToast: (text: string) => void;
}

interface PermissionToExtendForm {
  to: string;
  cc: string;
  body: string;
  bodyTemplate: string;
}

const emptyForm: PermissionToExtendForm = {
  to: "",
  cc: "",
  body: "",
  bodyTemplate: "",
};

const OUT_OF_TOWN = "OUT_OF_TOWN";
const CONSTRUCTION = "CONSTRUCTION";
const NO_AVAILABILITY = "NO_AVAILABILITY";

const UWPermissionToExtend = (props: UWPermissionToExtendProps) => {
  const mounted = useRef<boolean>(true);
  const formControlMethods = useRef<any>();
  const [formContent, setFormContent] = useState<PermissionToExtendForm>({ ...emptyForm });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [emailTemplates, setEmailTemplates] = useState<Record<string, string>>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(0);
  const [defaultEmail, setDefaultEmail] = useState<string>(null);
  const [defaultCC, setDefaultCC] = useState<string>(null);

  useEffect(() => {
    // TODO:
    getPermissionToExtendTemplates(
      props.controlIdNumber,
      (template) => {
        const next: Record<string, string> = {};
        for (const t of template.bodyTemplates) {
          next[t.key] = t.lines.join("\r\n");
        }
        setEmailTemplates(next);
        setDefaultEmail(template.to);
        setDefaultCC(template.cc);
        setFormContent({ ...emptyForm, to: template.to, cc: template.cc });
      },
      () => {},
      mounted
    );

    return () => {
      mounted.current = false;
    };
  }, []);

  const emailFormDefinition = [
    [
      {
        dataType: DATA_TYPE_STRING,
        label: "To:",
        source: "to",
        hasValidationError: validator.semiColonSeparatedEmailAddressesRequired,
      },
    ],
    [
      {
        dataType: DATA_TYPE_STRING,
        label: "CC:",
        source: "cc",
        hasValidationError: validator.semiColonSeparatedEmailAddresses,
      },
    ],
    [
      {
        dataType: DATA_TYPE_DROPDOWN2,
        label: "Message Template:",
        source: "bodyTemplate",
        hasValidationError: validator.required,
        dataTypeOptions: {
          options: [
            { label: "PH Out of Town", value: OUT_OF_TOWN },
            { label: "Construction at Risk", value: CONSTRUCTION },
            { label: "PH Request Due to Availability", value: NO_AVAILABILITY },
          ],
        },
        placeholder: "Select a template",
      },
    ],
    [
      {
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        label: "Message Body",
        source: "body",
        hasValidationError: validator.required,
      },
    ],
  ];

  const handleFormChange = (updatedObject: PermissionToExtendForm) => {
    let newFormContent = { ...updatedObject };

    if (defaultEmail && !newFormContent.to) {
      newFormContent.to = defaultEmail;
    } else {
      setDefaultEmail(null);
    }

    if (defaultCC && !newFormContent.cc) {
      newFormContent.cc = defaultCC;
    } else {
      setDefaultCC(null);
    }

    //If the template has changed, and we're not selecting the default bogus value, reset the message body.
    if (formContent.bodyTemplate !== updatedObject.bodyTemplate) {
      newFormContent.body = emailTemplates[newFormContent.bodyTemplate];
      setLastUpdate(Date.now());
    }

    setFormContent({ ...newFormContent });
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    let dto: PermissionToExtendStart = {
      to: formContent.to,
      cc: formContent.cc,
      body: formContent.body,
    };

    requestPermissionToExtend(
      props.controlIdNumber,
      dto,
      () => {
        setIsSubmitting(false);
        props.makeSuccessToast("Email Sent");
        if (props.sentCallback) {
          props.sentCallback();
        }
      },
      () => {
        setIsSubmitting(false);
      },
      mounted
    );
  };

  if (emailTemplates === null) return <MSpinner />;

  const onSend = () => {
    if (formControlMethods.current.isFormValid()) {
      onSubmit();
    }
  };

  return (
    <div className="uw-permission">
      <div>
        <MGridForm
          key={`email-form-${lastUpdate}`}
          data={formContent}
          onChange={handleFormChange}
          formDefinition={emailFormDefinition}
          getControlMethods={(cm) => {
            formControlMethods.current = cm;
          }}
          webform
        />
      </div>
      <div>
        <Button primary raised key="send-button" onClick={onSend} disabled={isSubmitting}>
          Send
        </Button>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(null, mapDispatchToProps)(UWPermissionToExtend);
